import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from './ui/card';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { toast } from 'sonner';
import { Upload, Camera, Check, Sparkles, X, ImagePlus } from 'lucide-react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const makeFileKey = (file) => `${file.name}-${file.size}-${file.lastModified}`;

const UploadPhotos = ({ onUploadSuccess }) => {
  const [uploaderName, setUploaderName] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  // Revoke object URLs when files are removed / component unmounts to avoid memory leaks
  useEffect(() => {
    return () => {
      selectedFiles.forEach((item) => URL.revokeObjectURL(item.previewUrl));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFileSelect = (e) => {
    const newFiles = Array.from(e.target.files);
    if (newFiles.length === 0) return;

    setSelectedFiles((prev) => {
      const existingKeys = new Set(prev.map((item) => item.key));
      const additions = newFiles
        .filter((file) => !existingKeys.has(makeFileKey(file)))
        .map((file) => ({
          file,
          key: makeFileKey(file),
          previewUrl: URL.createObjectURL(file),
        }));
      return [...prev, ...additions];
    });

    // Reset the input value so selecting the same file(s) again still fires onChange
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = (key) => {
    setSelectedFiles((prev) => {
      const target = prev.find((item) => item.key === key);
      if (target) {
        URL.revokeObjectURL(target.previewUrl);
      }
      return prev.filter((item) => item.key !== key);
    });
  };

  const handleClearAll = () => {
    selectedFiles.forEach((item) => URL.revokeObjectURL(item.previewUrl));
    setSelectedFiles([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!uploaderName.trim()) {
      toast.error('Please enter your name');
      return;
    }

    if (selectedFiles.length === 0) {
      toast.error('Please select at least one photo');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    const filesBeingUploaded = selectedFiles;

    try {
      const formData = new FormData();
      formData.append('uploader_name', uploaderName.trim());

      filesBeingUploaded.forEach((item) => {
        formData.append('files', item.file);
      });

      const response = await axios.post(
        `${API_BASE}/api/photos/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          // Generous timeout: guests may be on slow venue wifi uploading
          // several large photos at once. We'd rather wait than falsely
          // report a failure and risk a guest re-uploading duplicates.
          timeout: 5 * 60 * 1000,
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(percentCompleted);
          },
        }
      );

      // Results are returned in the same order the files were appended, so
      // we can pair them up positionally (safe even with duplicate names).
      const results = response.data;
      const successCount = results.filter(r => r.status === 'success').length;
      const failCount = results.filter(r => r.status === 'failed').length;

      // Only remove the files that actually succeeded from the selection.
      // Anything that failed stays selected (with its preview) so the guest
      // can simply hit "Share Photos" again -- no need to re-pick or
      // re-upload photos that already made it through.
      const stillPending = [];
      filesBeingUploaded.forEach((item, idx) => {
        const result = results[idx];
        if (result && result.status !== 'success') {
          stillPending.push(item);
        } else {
          URL.revokeObjectURL(item.previewUrl);
        }
      });
      setSelectedFiles(stillPending);

      if (successCount > 0) {
        toast.success(
          `Successfully uploaded ${successCount} ${successCount === 1 ? 'photo' : 'photos'}!`
        );

        if (onUploadSuccess) {
          onUploadSuccess();
        }

        setTimeout(() => {
          const galleryElement = document.querySelector('#gallery');
          if (galleryElement) {
            galleryElement.scrollIntoView({ behavior: 'smooth' });
          }
        }, 500);
      }

      if (failCount > 0) {
        const failedNames = results
          .filter(r => r.status === 'failed')
          .map(r => r.original_filename)
          .join(', ');
        toast.error(
          `${failCount} ${failCount === 1 ? 'photo' : 'photos'} failed (${failedNames}). Still selected below -- just hit "Share Photos" again to retry.`
        );
      }

      if (successCount === 0 && failCount === 0) {
        toast.error('Nothing was uploaded. Please try again.');
      }

      setUploadProgress(0);

    } catch (error) {
      console.error('Upload error:', error);
      // Keep every file selected on a network-level failure (e.g. timeout,
      // connection drop) -- nothing was confirmed lost, so don't clear
      // anything and let the guest simply retry.
      if (error.code === 'ECONNABORTED') {
        toast.error("Upload timed out -- your connection may be slow. Your photos are still selected, just try again.");
      } else {
        toast.error('Failed to upload photos. Your photos are still selected -- please try again.');
      }
    } finally {
      setUploading(false);
    }
  };

  const totalSize = selectedFiles.reduce((acc, item) => acc + item.file.size, 0);
  const totalSizeMB = (totalSize / (1024 * 1024)).toFixed(2);

  return (
    <section id="upload" className="py-24 bg-gradient-to-br from-primary via-primary/90 to-primary relative overflow-hidden" data-testid="upload-section">
      {/* Decorative pattern */}
      <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />
      
      <div className="section-container relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 space-y-6"
        >
          <div className="inline-flex items-center gap-2 px-6 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
            <Sparkles className="w-4 h-4 text-[#E6C76B]" />
            <span className="text-sm text-[#E6C76B] font-bold tracking-wider uppercase">Guest Book</span>
          </div>
          <h2 className="font-serif text-5xl sm:text-6xl font-normal text-white">Share Your Photos</h2>
          <div 
            className="h-1.5 w-32 mx-auto rounded-full"
            style={{ 
              backgroundImage: 'linear-gradient(135deg, #F9E27D 0%, #D4AF37 40%, #B8962E 60%, #F6E27A 100%)',
              boxShadow: '0 2px 8px rgba(212, 175, 55, 0.3)'
            }}
          />
          <p className="text-xl text-white/80 max-w-2xl mx-auto">
            Help us capture every magical moment from our celebration
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="max-w-2xl mx-auto"
        >
          <Card className="bg-white border-0 shadow-2xl rounded-3xl overflow-hidden">
            <div 
              className="bg-gradient-to-br from-[#EEF4F0] to-white p-8"
              style={{ 
                borderBottom: '4px solid transparent',
                borderImage: 'linear-gradient(135deg, #F9E27D 0%, #D4AF37 40%, #B8962E 60%, #F6E27A 100%) 1'
              }}
            >
              <CardTitle className="flex items-center justify-center gap-3 font-serif text-3xl font-normal text-primary">
                <Camera className="w-7 h-7 text-primary" />
                Upload Your Memories
              </CardTitle>
              <CardDescription className="mt-3 text-center text-base">
                Full high quality uploads (up to 25MB each) — select as many photos as you like
              </CardDescription>
            </div>
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-8">
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                      <span className="text-white font-bold text-sm">1</span>
                    </div>
                    <Label htmlFor="name" className="text-lg font-semibold text-primary">Your Name</Label>
                  </div>
                  <Input
                    id="name"
                    type="text"
                    placeholder="Enter your name"
                    value={uploaderName}
                    onChange={(e) => setUploaderName(e.target.value)}
                    disabled={uploading}
                    required
                    data-testid="upload-guest-name-input"
                    className="h-14 rounded-2xl border-2 border-primary/20 focus:border-primary text-lg"
                  />
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                        <span className="text-white font-bold text-sm">2</span>
                      </div>
                      <Label htmlFor="photos" className="text-lg font-semibold text-primary">Select Photos</Label>
                    </div>
                    {selectedFiles.length > 0 && !uploading && (
                      <button
                        type="button"
                        onClick={handleClearAll}
                        data-testid="upload-clear-all-button"
                        className="text-sm font-medium text-muted-foreground hover:text-destructive transition-colors focus-ring rounded-md px-2 py-1"
                      >
                        Clear all
                      </button>
                    )}
                  </div>
                  <div className="relative">
                    <Input
                      ref={fileInputRef}
                      id="photos"
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleFileSelect}
                      disabled={uploading}
                      data-testid="upload-photo-file-input"
                      className="h-14 rounded-2xl border-2 border-primary/20 cursor-pointer text-base"
                    />
                  </div>

                  {selectedFiles.length > 0 && (
                    <>
                      <div className="flex items-center gap-2 p-4 bg-[#EEF4F0] rounded-2xl">
                        <Check className="w-5 h-5 text-primary" />
                        <span className="font-medium text-primary">
                          {selectedFiles.length} {selectedFiles.length === 1 ? 'photo' : 'photos'} selected
                          <span className="text-muted-foreground ml-1">({totalSizeMB} MB)</span>
                        </span>
                      </div>

                      {/* Thumbnail previews with per-file remove */}
                      <div
                        className="grid grid-cols-3 sm:grid-cols-4 gap-3"
                        data-testid="upload-selected-files-preview"
                      >
                        <AnimatePresence>
                          {selectedFiles.map((item) => (
                            <motion.div
                              key={item.key}
                              initial={{ opacity: 0, scale: 0.85 }}
                              animate={{ opacity: 1, scale: 1 }}
                              exit={{ opacity: 0, scale: 0.85 }}
                              transition={{ duration: 0.2 }}
                              className="relative aspect-square rounded-xl overflow-hidden border-2 border-primary/15 group"
                              data-testid={`upload-preview-item-${item.key}`}
                            >
                              <img
                                src={item.previewUrl}
                                alt={item.file.name}
                                className="w-full h-full object-cover"
                              />
                              <button
                                type="button"
                                onClick={() => handleRemoveFile(item.key)}
                                disabled={uploading}
                                data-testid={`upload-remove-file-button-${item.key}`}
                                aria-label={`Remove ${item.file.name}`}
                                className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-black/60 hover:bg-destructive text-white flex items-center justify-center transition-colors duration-150 focus-ring disabled:opacity-40 disabled:pointer-events-none"
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </motion.div>
                          ))}
                        </AnimatePresence>
                        <button
                          type="button"
                          onClick={() => fileInputRef.current && fileInputRef.current.click()}
                          disabled={uploading}
                          data-testid="upload-add-more-button"
                          className="aspect-square rounded-xl border-2 border-dashed border-primary/30 hover:border-primary/60 flex flex-col items-center justify-center gap-1 text-primary transition-colors duration-150 focus-ring disabled:opacity-40 disabled:pointer-events-none"
                        >
                          <ImagePlus className="w-5 h-5" />
                          <span className="text-xs font-medium">Add more</span>
                        </button>
                      </div>
                    </>
                  )}
                </div>

                {uploading && uploadProgress > 0 && (
                  <div className="space-y-3 p-6 bg-[#EEF4F0] rounded-2xl" data-testid="upload-progress">
                    <div className="flex justify-between text-base font-medium text-primary">
                      <span>Uploading your memories...</span>
                      <span>{uploadProgress}%</span>
                    </div>
                    <Progress value={uploadProgress} className="h-3 bg-white" />
                  </div>
                )}

                <Button
                  type="submit"
                  size="lg"
                  className="w-full h-16 rounded-2xl text-lg font-semibold bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 text-white shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all duration-300"
                  disabled={uploading || !uploaderName.trim() || selectedFiles.length === 0}
                  data-testid="upload-submit-button"
                >
                  {uploading ? (
                    <span className="flex items-center gap-3">
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      <span>Uploading...</span>
                    </span>
                  ) : (
                    <span className="flex items-center gap-3">
                      <Upload className="w-5 h-5" />
                      Share Photos
                    </span>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </section>
  );
};

export default UploadPhotos;
